import { Box, xcss } from '@atlaskit/primitives';
import React, { type FC, useEffect, useState, useRef, useMemo } from 'react';

import {
  FIELD_WIDTH_INLINE_THRESHOLD,
  MAX_POPOVER_EDIT_WIDTH,
} from './popover-edit-common';

type PopoverEditProps = {
  isMultiSelect?: boolean;
  currentDataSection?: React.ReactNode;
  addDataSection: React.ReactNode;
  hasCurrentData: boolean;
  additionalStyles?: React.CSSProperties;
  isRenderedInSidePanel?: boolean;
  fieldWidth?: number;
};

const ReferenceCellStyles = xcss({
  display: 'block',
  position: 'relative',
  height: '100%',
  borderWidth: 'border.width.outline',
  borderColor: 'color.border.focused',
  borderStyle: 'solid',
  borderRadius: 'border.radius',
});

const DefaultReferenceCellStyles = xcss({
  borderColor: 'color.border.input',
});

const PopoverContainerStyles = xcss({
  display: 'block',
  position: 'absolute',
  width: 'fit-content',
  zIndex: 'modal',
  boxShadow: 'elevation.shadow.overlay',
  borderRadius: 'border.radius',
  minWidth: '260px',
  maxWidth: `${MAX_POPOVER_EDIT_WIDTH}px`,
});

const PopoverContainerOffsetRightStyles = xcss({
  right: 'space.100',
});

const PopoverContainerOffsetLeftStyles = xcss({
  left: 'space.100',
});

const PopoverContainerTopStyles = xcss({
  bottom: 'space.100',
});

const PopoverContainerBottomStyles = xcss({
  top: 'space.100',
});

const PopoverContainerFilterViewStyles = xcss({
  minWidth: 'auto !important',
  position: 'relative',
  top: '0',
  right: '0',
  left: '0',
  width: '100%',
});

const CurrentDataStyles = xcss({
  backgroundColor: 'elevation.surface.sunken',
  borderRadius: 'inherit',
  padding: 'space.100',
});

const MultiSelectCurrentDataContainerStyles = xcss({
  borderBottomColor: 'color.border',
  borderBottomWidth: 'border.width',
  borderBottomStyle: 'solid',
  borderBottomLeftRadius: '0',
  borderBottomRightRadius: '0',
});

const AddDataStyles = xcss({
  backgroundColor: 'color.background.input',
  borderRadius: 'inherit',
  padding: 'space.150',
});

const MultiSelectAddDataContainerStyles = xcss({
  borderTopLeftRadius: '0',
  borderTopRightRadius: '0',
});

const InlineWidthStyles = xcss({
  maxWidth: 'none',
  minWidth: 'none',
  width: '100%',
  top: '0',
  right: '0',
  left: '0',
});

const updatePopupPosition = (
  popoverElement: HTMLElement,
  setHorizontalPosition: React.Dispatch<React.SetStateAction<string>>,
  setVerticalPosition: React.Dispatch<React.SetStateAction<string>>
) => {
  const tableContainer = document.getElementById('scrollable-inner-container');

  if (tableContainer) {
    const popoverRect = popoverElement.getBoundingClientRect();
    const containerRect = tableContainer.getBoundingClientRect();

    // Check horizontal overflow
    if (popoverRect.right >= containerRect.right) {
      setHorizontalPosition('right');
    }

    // Check vertical overflow
    if (popoverRect.bottom >= containerRect.bottom) {
      setVerticalPosition('top');
    }
  }
};

const stopEvent = (e: React.MouseEvent | React.KeyboardEvent) => {
  e.stopPropagation();
  e.preventDefault();
};

export const PopoverEdit: FC<PopoverEditProps> = ({
  isMultiSelect,
  currentDataSection,
  hasCurrentData,
  addDataSection,
  additionalStyles,
  isRenderedInSidePanel,
  fieldWidth,
}) => {
  // TODO - We should introduce a more flexible position for the popover
  const [horizontalPosition, setHorizontalPosition] = useState('left');
  const [verticalPosition, setVerticalPosition] = useState('bottom');
  const isWidthInline = fieldWidth ? fieldWidth > FIELD_WIDTH_INLINE_THRESHOLD : false;

  const popoverRef = useRef(null);

  useEffect(() => {
    const popoverElement = popoverRef.current;
    if (!popoverElement) {
      return;
    }

    const resizeObserver = new ResizeObserver(() => {
      updatePopupPosition(popoverElement, setHorizontalPosition, setVerticalPosition);
    });

    resizeObserver.observe(popoverElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const popoverSections = useMemo(() => {
    return (
      <>
        {isMultiSelect ? (
          <>
            {hasCurrentData && (
              <Box
                xcss={[CurrentDataStyles, MultiSelectCurrentDataContainerStyles]}
                testId="popover-edit-container-multi-select-value"
                id={'popover-edit-container-multi-select-value'}
              >
                {currentDataSection}
              </Box>
            )}
            <Box
              xcss={[AddDataStyles, hasCurrentData && MultiSelectAddDataContainerStyles]}
              testId="popover-edit-container-value-addData"
            >
              {addDataSection}
            </Box>
          </>
        ) : (
          <Box
            xcss={[hasCurrentData ? CurrentDataStyles : AddDataStyles]}
            testId={
              hasCurrentData
                ? 'popover-edit-container-value-currentData'
                : 'popover-edit-container-value-addData'
            }
          >
            {hasCurrentData ? currentDataSection : addDataSection}
          </Box>
        )}
      </>
    );
  }, [addDataSection, currentDataSection, hasCurrentData, isMultiSelect]);

  const popover = useMemo(() => {
    return (
      <Box
        testId="popover-edit-container"
        ref={popoverRef}
        xcss={[
          PopoverContainerStyles,
          horizontalPosition === 'right'
            ? PopoverContainerOffsetRightStyles
            : PopoverContainerOffsetLeftStyles,
          verticalPosition === 'top'
            ? PopoverContainerTopStyles
            : PopoverContainerBottomStyles,
          isRenderedInSidePanel && PopoverContainerFilterViewStyles,
          isWidthInline && InlineWidthStyles,
        ]}
        style={additionalStyles}
        /**
         * This specifically solves an issue in Safari where if you tried to click anywhere within the
         * popover's currentDataSection, the click event was bubbling up to the parent and being
         * handled as if the user was clicking outside of the popover leading to it
         * closing before any action could occur.
         */
        onMouseDown={stopEvent}
      >
        {popoverSections}
      </Box>
    );
  }, [
    additionalStyles,
    isRenderedInSidePanel,
    horizontalPosition,
    popoverSections,
    verticalPosition,
    isWidthInline,
  ]);

  return (
    <Box
      xcss={[ReferenceCellStyles, isRenderedInSidePanel && DefaultReferenceCellStyles]}
    >
      {isRenderedInSidePanel ? popoverSections : popover}
    </Box>
  );
};
